import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { RefreshCw, Download, Trash2, Search, Users } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import usePageMeta from '../hooks/usePageMeta';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
};

const StatCard = ({ label, value }) => (
  <div className="bg-white border border-neutral-200 p-6">
    <p className="text-xs uppercase tracking-widest text-neutral-500 mb-2">{label}</p>
    <p className="font-serif text-3xl text-neutral-900">{value}</p>
  </div>
);

const SurveyResults = () => {
  const { toast } = useToast();
  usePageMeta({
    title: 'Survey Results (Admin)',
    description: 'Admin dashboard for survey responses.',
    path: '/survey-results',
    noindex: true,
  });
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/survey`);
      // Exclude male responses from the tally
      const all = res.data || [];
      const visible = all.filter((r) => r.gender?.toLowerCase() !== 'male');
      setResponses(visible);
    } catch (e) {
      toast({ title: 'Failed to load responses', description: e.message });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/survey/${id}`);
      setResponses(responses.filter((r) => r.id !== id));
      toast({ title: 'Response deleted' });
    } catch (e) {
      toast({ title: 'Delete failed', description: e.message });
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return responses;
    return responses.filter(
      (r) =>
        String(r.age).includes(q) ||
        r.gender?.toLowerCase().includes(q) ||
        r.city?.toLowerCase().includes(q) ||
        r.spanking_hardness?.toLowerCase().includes(q) ||
        r.how_bared?.toLowerCase().includes(q) ||
        r.position?.toLowerCase().includes(q) ||
        r.implement?.toLowerCase().includes(q) ||
        r.most_important?.toLowerCase().includes(q) ||
        r.aftercare?.toLowerCase().includes(q) ||
        r.safe_word?.toLowerCase().includes(q) ||
        r.interested_in_session?.toLowerCase().includes(q) ||
        r.contact_preference?.toLowerCase().includes(q) ||
        r.contact_email?.toLowerCase().includes(q)
    );
  }, [query, responses]);

  const stats = useMemo(() => {
    const total = responses.length;
    const avgAge =
      total > 0
        ? Math.round(responses.reduce((s, r) => s + (r.age || 0), 0) / total)
        : 0;
    const interested = responses.filter((r) => r.interested_in_session?.toLowerCase() === 'yes').length;
    const withEmail = responses.filter((r) => (r.contact_email || '').trim().length > 0).length;
    return { total, avgAge, interested, withEmail };
  }, [responses]);

  const exportCsv = () => {
    if (responses.length === 0) return;
    const header = [
      'Submitted At',
      'Age',
      'Gender',
      'City',
      'Hardness',
      'Bared',
      'Position',
      'Implement',
      'Most Important',
      'Aftercare',
      'Safe Word',
      'Interested in Session',
      'Contact Preference',
      'Contact Email',
    ];
    const rows = responses.map((r) => [
      formatDate(r.submitted_at),
      r.age,
      r.gender,
      r.city,
      r.spanking_hardness || '',
      r.how_bared || '',
      r.position || '',
      r.implement || '',
      r.most_important || '',
      r.aftercare || '',
      r.safe_word || '',
      r.interested_in_session || '',
      r.contact_preference || '',
      r.contact_email || '',
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `survey-responses-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <PageHeader
        eyebrow="Admin"
        title="Survey Results"
        subtitle="All submitted responses. Keep this URL private."
        image="https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=3840"
      />
      <section className="bg-neutral-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 md:py-20">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <StatCard label="Total Responses" value={stats.total} />
            <StatCard label="Average Age" value={stats.avgAge || '—'} />
            <StatCard label="Interested in Session" value={stats.interested} />
            <StatCard label="Provided Email" value={stats.withEmail} />
          </div>

          {/* Toolbar */}
          <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
            <div className="relative flex-1">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
              />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by age, gender, or city"
                className="pl-10 h-11 rounded-none border-neutral-300 bg-white focus-visible:ring-neutral-900"
              />
            </div>
            <Button
              onClick={load}
              variant="outline"
              className="rounded-none border-neutral-300 h-11 bg-white"
            >
              <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button
              onClick={exportCsv}
              disabled={responses.length === 0}
              className="rounded-none bg-neutral-900 text-white hover:bg-neutral-700 h-11"
            >
              <Download size={16} className="mr-2" /> Export CSV
            </Button>
          </div>

          {/* Table */}
          <div className="bg-white border border-neutral-200 overflow-x-auto">
            {(() => {
              if (loading) {
                return <div className="p-12 text-center text-neutral-500">Loading responses...</div>;
              }
              if (filtered.length === 0) {
                const emptyMessage = responses.length === 0
                  ? 'No survey responses yet.'
                  : 'No responses match your search.';
                return (
                  <div className="p-16 text-center">
                    <Users size={40} className="mx-auto text-neutral-300 mb-4" strokeWidth={1.5} />
                    <p className="text-neutral-600 font-serif text-xl">{emptyMessage}</p>
                    {responses.length === 0 && (
                      <p className="text-neutral-500 text-sm mt-2">
                        Share the /survey link with clients to gather feedback.
                      </p>
                    )}
                  </div>
                );
              }
              return (
              <table className="w-full text-left min-w-[2050px]">
                <thead className="bg-neutral-100 text-xs uppercase tracking-widest text-neutral-600">
                  <tr>
                    <th className="px-4 py-4 whitespace-nowrap">Submitted</th>
                    <th className="px-4 py-4 whitespace-nowrap">Age</th>
                    <th className="px-4 py-4 whitespace-nowrap">Gender</th>
                    <th className="px-4 py-4 whitespace-nowrap">City</th>
                    <th className="px-4 py-4 w-48">Hardness</th>
                    <th className="px-4 py-4 w-48">Bared</th>
                    <th className="px-4 py-4 w-48">Position</th>
                    <th className="px-4 py-4 w-40">Implement</th>
                    <th className="px-4 py-4 w-48">Most Important</th>
                    <th className="px-4 py-4 w-56">Aftercare</th>
                    <th className="px-4 py-4 whitespace-nowrap">Safe Word</th>
                    <th className="px-4 py-4 whitespace-nowrap">Interested</th>
                    <th className="px-4 py-4 w-40">Contact Pref</th>
                    <th className="px-4 py-4 w-48">Contact Email</th>
                    <th className="px-4 py-4 text-right whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} className="border-t border-neutral-100 hover:bg-neutral-50 align-top">
                      <td className="px-4 py-4 text-sm text-neutral-600 whitespace-nowrap">
                        {formatDate(r.submitted_at)}
                      </td>
                      <td className="px-4 py-4 font-medium text-neutral-900">{r.age}</td>
                      <td className="px-4 py-4 text-neutral-700">{r.gender}</td>
                      <td className="px-4 py-4 text-neutral-700">{r.city}</td>
                      <td className="px-4 py-4 text-neutral-700 text-sm">{r.spanking_hardness || '—'}</td>
                      <td className="px-4 py-4 text-neutral-700 text-sm">{r.how_bared || '—'}</td>
                      <td className="px-4 py-4 text-neutral-700 text-sm">{r.position || '—'}</td>
                      <td className="px-4 py-4 text-neutral-700 text-sm">{r.implement || '—'}</td>
                      <td className="px-4 py-4 text-neutral-700 text-sm">{r.most_important || '—'}</td>
                      <td className="px-4 py-4 text-neutral-700 text-sm">{r.aftercare || '—'}</td>
                      <td className="px-4 py-4 text-neutral-700 text-sm whitespace-nowrap">{r.safe_word || '—'}</td>
                      <td className="px-4 py-4 text-sm whitespace-nowrap">
                        {r.interested_in_session ? (
                          <span className={`inline-block px-2 py-1 text-xs font-medium ${r.interested_in_session.toLowerCase() === 'yes' ? 'bg-green-100 text-green-800' : 'bg-neutral-100 text-neutral-700'}`}>
                            {r.interested_in_session}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-4 text-sm text-neutral-700">{r.contact_preference || '—'}</td>
                      <td className="px-4 py-4 text-sm">
                        {r.contact_email ? (
                          <a href={`mailto:${r.contact_email}`} className="text-neutral-900 underline underline-offset-2 hover:text-neutral-600 break-all">
                            {r.contact_email}
                          </a>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-4 text-right whitespace-nowrap">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-neutral-500 hover:text-red-600 hover:bg-red-50 rounded-none"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete response?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently remove this survey response.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(r.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              );
            })()}
          </div>

          <p className="text-xs text-neutral-500 mt-6">
            Showing {filtered.length} of {responses.length} responses
          </p>
        </div>
      </section>
    </div>
  );
};

export default SurveyResults;
